/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import type {CustomStatus, GatewayCustomStatusPayload} from '@app/lib/CustomStatus';
import {fromGatewayCustomStatus} from '@app/lib/CustomStatus';
import {CustomStatusEmitter} from '@app/lib/CustomStatusEmitter';
import {Logger} from '@app/lib/Logger';
import type {GuildMemberRecord} from '@app/records/GuildMemberRecord';
import GuildMemberStore from '@app/stores/GuildMemberStore';
import GuildStore from '@app/stores/GuildStore';
import GatewayConnectionStore from '@app/stores/gateway/GatewayConnectionStore';
import WindowStore from '@app/stores/WindowStore';
import {
	buildMemberListLayout,
	getGroupLayoutForRow,
	getTotalMemberCount,
	getTotalRowsFromLayout,
} from '@app/utils/MemberListLayout';
import {GuildOperations} from '@fluxer/constants/src/GuildConstants';
import type {StatusType} from '@fluxer/constants/src/StatusConstants';
import {StatusTypes} from '@fluxer/constants/src/StatusConstants';
import {makeAutoObservable} from 'mobx';

interface MemberListGroup {
	id: string;
	count: number;
}

interface MemberListItem {
	type: 'member';
	data: GuildMemberRecord;
}

interface MemberListState {
	memberCount: number;
	onlineCount: number;
	groups: Array<MemberListGroup>;
	rows: Map<number, MemberListRow>;
	items: Map<number, MemberListItem>;
	subscribedRanges: Array<[number, number]>;
	presences: Map<string, StatusType>;
	customStatuses: Map<string, CustomStatus | null>;
}

interface MemberListRow {
	type: 'group' | 'member';
	group?: MemberListGroup;
	userId?: string;
	presence?: {status?: string; custom_status?: GatewayCustomStatusPayload | null} | null;
}

interface MemberListOperation {
	op: 'SYNC' | 'INSERT' | 'UPDATE' | 'DELETE' | 'INVALIDATE';
	range?: [number, number];
	items?: Array<{
		member?: {
			user: {id: string};
			presence?: {status?: string; custom_status?: GatewayCustomStatusPayload | null} | null;
		};
		group?: MemberListGroup;
	}>;
	index?: number;
	item?: {
		member?: {
			user: {id: string};
			presence?: {status?: string; custom_status?: GatewayCustomStatusPayload | null} | null;
		};
		group?: MemberListGroup;
	};
}

const MEMBER_LIST_TTL_MS = 5 * 60 * 1000;
const MEMBER_LIST_PRUNE_INTERVAL_MS = 30 * 1000;
const MEMBER_LIST_RESYNC_COOLDOWN_MS = 5000;
const MEMBER_LIST_RESYNC_MIN_INCOMPLETE_STREAK = 2;
const DEFAULT_MEMBER_LIST_RESYNC_RANGE: Array<[number, number]> = [[0, 99]];

function areRangesEqual(left?: Array<[number, number]>, right?: Array<[number, number]>): boolean {
	const leftRanges = left ?? [];
	const rightRanges = right ?? [];

	if (leftRanges.length !== rightRanges.length) {
		return false;
	}

	for (let i = 0; i < leftRanges.length; i++) {
		const [leftStart, leftEnd] = leftRanges[i];
		const [rightStart, rightEnd] = rightRanges[i];
		if (leftStart !== rightStart || leftEnd !== rightEnd) {
			return false;
		}
	}

	return true;
}

class MemberSidebarStore {
	private logger = new Logger('MemberSidebarStore', 1);
	lists: Record<string, Record<string, MemberListState>> = {};
	channelListIds: Record<string, Record<string, string>> = {};
	lastAccess: Record<string, Record<string, number>> = {};
	pruneIntervalId: number | null = null;
	pendingMemberLoads: Record<string, Set<string>> = {};
	lastCompleteItems: Record<string, Record<string, Map<number, MemberListItem>>> = {};
	incompleteListStreaks: Record<string, Record<string, number>> = {};
	lastResyncAttemptAt: Record<string, Record<string, number>> = {};
	sessionVersion = 0;

	constructor() {
		makeAutoObservable(
			this,
			{
				lastAccess: false,
				pruneIntervalId: false,
				pendingMemberLoads: false,
				lastCompleteItems: false,
				incompleteListStreaks: false,
				lastResyncAttemptAt: false,
			},
			{autoBind: true},
		);
		this.startPruneInterval();
	}

	handleSessionInvalidated(): void {
		this.lists = {};
		this.channelListIds = {};
		this.lastAccess = {};
		this.pendingMemberLoads = {};
		this.lastCompleteItems = {};
		this.incompleteListStreaks = {};
		this.lastResyncAttemptAt = {};
		this.sessionVersion += 1;
	}

	handleGuildDelete(guildId: string): void {
		if (this.lists[guildId]) {
			const {[guildId]: _, ...remainingLists} = this.lists;
			this.lists = remainingLists;
		}
		if (this.channelListIds[guildId]) {
			const {[guildId]: _, ...remainingMappings} = this.channelListIds;
			this.channelListIds = remainingMappings;
		}
		if (this.lastAccess[guildId]) {
			const {[guildId]: _, ...remainingAccess} = this.lastAccess;
			this.lastAccess = remainingAccess;
		}
		if (this.pendingMemberLoads[guildId]) {
			const {[guildId]: _, ...remainingPendingLoads} = this.pendingMemberLoads;
			this.pendingMemberLoads = remainingPendingLoads;
		}
		if (this.lastCompleteItems[guildId]) {
			const {[guildId]: _, ...remainingLastCompleteItems} = this.lastCompleteItems;
			this.lastCompleteItems = remainingLastCompleteItems;
		}
		if (this.incompleteListStreaks[guildId]) {
			const {[guildId]: _, ...remainingIncompleteStreaks} = this.incompleteListStreaks;
			this.incompleteListStreaks = remainingIncompleteStreaks;
		}
		if (this.lastResyncAttemptAt[guildId]) {
			const {[guildId]: _, ...remainingResyncAttemptAt} = this.lastResyncAttemptAt;
			this.lastResyncAttemptAt = remainingResyncAttemptAt;
		}
	}

	handleGuildCreate(guildId: string): void {
		if (this.lists[guildId]) {
			const {[guildId]: _, ...remainingLists} = this.lists;
			this.lists = remainingLists;
		}
		if (this.channelListIds[guildId]) {
			const {[guildId]: _, ...remainingMappings} = this.channelListIds;
			this.channelListIds = remainingMappings;
		}
		if (this.lastAccess[guildId]) {
			const {[guildId]: _, ...remainingAccess} = this.lastAccess;
			this.lastAccess = remainingAccess;
		}
		if (this.pendingMemberLoads[guildId]) {
			const {[guildId]: _, ...remainingPendingLoads} = this.pendingMemberLoads;
			this.pendingMemberLoads = remainingPendingLoads;
		}
		if (this.lastCompleteItems[guildId]) {
			const {[guildId]: _, ...remainingLastCompleteItems} = this.lastCompleteItems;
			this.lastCompleteItems = remainingLastCompleteItems;
		}
		if (this.incompleteListStreaks[guildId]) {
			const {[guildId]: _, ...remainingIncompleteStreaks} = this.incompleteListStreaks;
			this.incompleteListStreaks = remainingIncompleteStreaks;
		}
		if (this.lastResyncAttemptAt[guildId]) {
			const {[guildId]: _, ...remainingResyncAttemptAt} = this.lastResyncAttemptAt;
			this.lastResyncAttemptAt = remainingResyncAttemptAt;
		}
	}

	handleListUpdate(params: {
		guildId: string;
		listId: string;
		channelId?: string;
		memberCount: number;
		onlineCount: number;
		groups: Array<MemberListGroup>;
		ops: Array<MemberListOperation>;
	}): void {
		const {guildId, listId, channelId, memberCount, onlineCount, groups, ops} = params;
		if (this.isMemberListUpdatesDisabled(guildId)) {
			return;
		}

		this.logger.debug('Member list update received', {
			guildId,
			listId,
			channelId: channelId ?? null,
			memberCount,
			onlineCount,
			groupCount: groups.length,
			groups: groups.map((group) => ({id: group.id, count: group.count})),
			opsCount: ops.length,
			opTypes: ops.map((op) => op.op),
		});

		const storageKey = listId;
		const existingGuildLists = this.lists[guildId] ?? {};
		const guildLists: Record<string, MemberListState> = {...existingGuildLists};

		if (channelId) {
			this.registerChannelListId(guildId, channelId, listId);
			if (guildLists[channelId] && !guildLists[storageKey]) {
				guildLists[storageKey] = guildLists[channelId];
				delete guildLists[channelId];
			}
		}

		if (!guildLists[storageKey]) {
			guildLists[storageKey] = {
				memberCount: 0,
				onlineCount: 0,
				groups: [],
				rows: new Map(),
				items: new Map(),
				subscribedRanges: [],
				presences: new Map(),
				customStatuses: new Map(),
			};
		}

		const listState = guildLists[storageKey];
		const newRows = new Map(listState.rows);
		const changedCustomStatusUserIds = new Set<string>();

		this.touchList(guildId, storageKey);

		for (const op of ops) {
			switch (op.op) {
				case 'SYNC': {
					if (op.range && op.items) {
						const [start, end] = op.range;
						for (let i = start; i <= end; i++) {
							newRows.delete(i);
						}
						let nextIndex = start;
						for (const rawItem of op.items) {
							const row = this.convertRow(rawItem);
							if (row) {
								newRows.set(nextIndex, row);
							}
							nextIndex += 1;
						}
					}
					break;
				}
				case 'INSERT': {
					if (op.index !== undefined && op.item) {
						const shiftedRows = new Map<number, MemberListRow>();
						for (const [index, existingRow] of newRows) {
							if (index >= op.index) {
								shiftedRows.set(index + 1, existingRow);
							} else {
								shiftedRows.set(index, existingRow);
							}
						}

						const row = this.convertRow(op.item);
						if (row) {
							shiftedRows.set(op.index, row);
						}

						newRows.clear();
						for (const [k, v] of shiftedRows) {
							newRows.set(k, v);
						}
					}
					break;
				}
				case 'UPDATE': {
					if (op.index !== undefined && op.item) {
						const row = this.convertRow(op.item);
						if (row) {
							newRows.set(op.index, row);
						} else {
							newRows.delete(op.index);
						}
					}
					break;
				}
				case 'DELETE': {
					if (op.index !== undefined) {
						const shiftedRows = new Map<number, MemberListRow>();
						for (const [index, existingRow] of newRows) {
							if (index > op.index) {
								shiftedRows.set(index - 1, existingRow);
							} else if (index !== op.index) {
								shiftedRows.set(index, existingRow);
							}
						}
						newRows.clear();
						for (const [k, v] of shiftedRows) {
							newRows.set(k, v);
						}
					}
					break;
				}
				case 'INVALIDATE': {
					if (op.range) {
						const [start, end] = op.range;
						for (let i = start; i <= end; i++) {
							newRows.delete(i);
						}
					}
					break;
				}
			}
		}

		const groupLayouts = buildMemberListLayout(groups);
		const totalMembers = Math.max(memberCount, getTotalMemberCount(groups));
		const totalRows = groupLayouts.length > 0 ? getTotalRowsFromLayout(groupLayouts) : totalMembers;
		const boundedRows = new Map<number, MemberListRow>();
		const droppedRowIndexes: Array<number> = [];
		for (const [index, row] of newRows) {
			if (index < 0 || index >= totalRows) {
				droppedRowIndexes.push(index);
				continue;
			}
			boundedRows.set(index, row);
		}
		if (droppedRowIndexes.length > 0) {
			this.logger.warn('Dropped member list rows outside computed bounds', {
				guildId,
				listId: storageKey,
				totalRows,
				droppedCount: droppedRowIndexes.length,
				droppedRowIndexesSample: droppedRowIndexes.slice(0, 25),
			});
		}

		const {items: newItems, presences: newPresences, customStatuses: newCustomStatuses, duplicateUserIds, missingUserIds} =
			this.buildListStateEntries({
				guildId,
				rows: boundedRows,
				groupLayouts,
				totalMembers,
			});
		const continuityItems = this.applyContinuityBackfill({
			guildId,
			listId: storageKey,
			nextItems: newItems,
			previousItems: listState.items,
			totalMembers,
			hasDuplicateRows: duplicateUserIds.length > 0,
		});

		this.logger.debug('Member list state built', {
			guildId,
			listId: storageKey,
			channelId: channelId ?? null,
			totalMembers,
			totalRows,
			boundedRows: boundedRows.size,
			resolvedItems: continuityItems.size,
			presenceCount: newPresences.size,
			customStatusCount: newCustomStatuses.size,
			duplicateCount: duplicateUserIds.length,
			missingCount: missingUserIds.length,
			missingUserIdsSample: missingUserIds.slice(0, 25),
		});

		const previousCustomStatuses = listState.customStatuses;
		const allCustomStatusUserIds = new Set<string>([
			...Array.from(previousCustomStatuses.keys()),
			...Array.from(newCustomStatuses.keys()),
		]);
		for (const userId of allCustomStatusUserIds) {
			const previousCustomStatus = previousCustomStatuses.has(userId)
				? (previousCustomStatuses.get(userId) ?? null)
				: undefined;
			const nextCustomStatus = newCustomStatuses.has(userId) ? (newCustomStatuses.get(userId) ?? null) : undefined;
			if (previousCustomStatus !== nextCustomStatus) {
				changedCustomStatusUserIds.add(userId);
			}
		}

		listState.memberCount = memberCount;
		listState.onlineCount = onlineCount;
		listState.groups = groups;
		listState.rows = boundedRows;
		listState.items = continuityItems;
		listState.presences = newPresences;
		listState.customStatuses = newCustomStatuses;

		this.lists = {...this.lists, [guildId]: {...guildLists, [storageKey]: listState}};
		this.captureLastCompleteItemsSnapshot(guildId, storageKey, continuityItems, totalMembers);
		this.handlePotentialListInconsistency({
			guildId,
			listId: storageKey,
			channelId: channelId ?? null,
			totalMembers,
			resolvedItems: continuityItems.size,
			duplicateUserIds,
			listState,
		});

		if (duplicateUserIds.length > 0) {
			const uniqueDuplicateUserIds = Array.from(new Set(duplicateUserIds));
			this.logger.warn('Duplicate member rows received in list update:', {
				guildId,
				listId: storageKey,
				duplicateCount: uniqueDuplicateUserIds.length,
				userIds: uniqueDuplicateUserIds.slice(0, 25),
			});
		}

		if (missingUserIds.length > 0) {
			this.queueMissingMemberLoad(guildId, storageKey, missingUserIds);
		}

		if (changedCustomStatusUserIds.size > 0) {
			queueMicrotask(() => {
				for (const userId of changedCustomStatusUserIds) {
					CustomStatusEmitter.emitMemberListChange(guildId, storageKey, userId);
				}
			});
		}
	}

	private applyContinuityBackfill(params: {
		guildId: string;
		listId: string;
		nextItems: Map<number, MemberListItem>;
		previousItems: Map<number, MemberListItem>;
		totalMembers: number;
		hasDuplicateRows: boolean;
	}): Map<number, MemberListItem> {
		const {guildId, listId, nextItems, previousItems, totalMembers, hasDuplicateRows} = params;
		if (nextItems.size >= totalMembers) {
			return nextItems;
		}

		const mergedItems = new Map(nextItems);
		const currentUserIds = new Set(Array.from(mergedItems.values()).map((item) => item.data.user.id));
		let backfilled = 0;

		const fillFromSource = (sourceItems: Map<number, MemberListItem>): void => {
			if (mergedItems.size >= totalMembers) {
				return;
			}

			const sourceEntries = Array.from(sourceItems.entries()).sort(([left], [right]) => left - right);
			for (const [memberIndex, sourceItem] of sourceEntries) {
				if (memberIndex < 0 || memberIndex >= totalMembers) {
					continue;
				}
				if (mergedItems.has(memberIndex)) {
					continue;
				}

				const sourceUserId = sourceItem.data.user.id;
				if (currentUserIds.has(sourceUserId)) {
					continue;
				}
				if (!GuildMemberStore.getMember(guildId, sourceUserId)) {
					continue;
				}

				mergedItems.set(memberIndex, sourceItem);
				currentUserIds.add(sourceUserId);
				backfilled += 1;

				if (mergedItems.size >= totalMembers) {
					break;
				}
			}
		};

		fillFromSource(previousItems);

		if (mergedItems.size < totalMembers) {
			const stableItems = this.lastCompleteItems[guildId]?.[listId];
			if (stableItems) {
				fillFromSource(stableItems);
			}
		}
		if (mergedItems.size < totalMembers && hasDuplicateRows) {
			const stableItems = this.lastCompleteItems[guildId]?.[listId];
			if (stableItems && stableItems.size >= totalMembers) {
				this.logger.warn('Using last complete member list snapshot due to duplicate-heavy update', {
					guildId,
					listId,
					nextItems: nextItems.size,
					totalMembers,
				});
				return new Map(stableItems);
			}
		}

		if (backfilled > 0) {
			this.logger.warn('Applied member list continuity backfill after inconsistent gateway update', {
				guildId,
				backfilled,
				nextItems: nextItems.size,
				mergedItems: mergedItems.size,
				totalMembers,
			});
		}
		if (mergedItems.size < totalMembers) {
			this.logger.warn('Member list still incomplete after continuity backfill', {
				guildId,
				listId,
				nextItems: nextItems.size,
				mergedItems: mergedItems.size,
				totalMembers,
			});
		}

		return mergedItems;
	}

	private handlePotentialListInconsistency(params: {
		guildId: string;
		listId: string;
		channelId: string | null;
		totalMembers: number;
		resolvedItems: number;
		duplicateUserIds: Array<string>;
		listState: MemberListState;
	}): void {
		const {guildId, listId, channelId, totalMembers, resolvedItems, duplicateUserIds, listState} = params;

		if (resolvedItems >= totalMembers) {
			this.setIncompleteListStreak(guildId, listId, 0);
			return;
		}

		const nextStreak = this.getIncompleteListStreak(guildId, listId) + 1;
		this.setIncompleteListStreak(guildId, listId, nextStreak);

		if (duplicateUserIds.length === 0 || nextStreak < MEMBER_LIST_RESYNC_MIN_INCOMPLETE_STREAK) {
			return;
		}

		const now = Date.now();
		const lastAttemptAt = this.lastResyncAttemptAt[guildId]?.[listId] ?? 0;
		if (now - lastAttemptAt < MEMBER_LIST_RESYNC_COOLDOWN_MS) {
			return;
		}

		const resolvedChannelId = channelId ?? this.getChannelIdByListId(guildId, listId) ?? listId;
		const resyncRanges =
			listState.subscribedRanges.length > 0 ? listState.subscribedRanges : DEFAULT_MEMBER_LIST_RESYNC_RANGE;

		this.logger.warn('Triggering member list resync after repeated incomplete duplicate updates', {
			guildId,
			listId,
			channelId: resolvedChannelId,
			streak: nextStreak,
			resolvedItems,
			totalMembers,
			duplicateCount: new Set(duplicateUserIds).size,
			ranges: resyncRanges,
		});

		const socket = GatewayConnectionStore.socket;
		socket?.updateGuildSubscriptions({
			subscriptions: {
				[guildId]: {
					member_list_channels: {[resolvedChannelId]: resyncRanges},
				},
			},
		});

		const guildAttempts = this.lastResyncAttemptAt[guildId] ?? {};
		this.lastResyncAttemptAt = {
			...this.lastResyncAttemptAt,
			[guildId]: {
				...guildAttempts,
				[listId]: now,
			},
		};
	}

	private captureLastCompleteItemsSnapshot(
		guildId: string,
		listId: string,
		items: Map<number, MemberListItem>,
		totalMembers: number,
	): void {
		if (items.size < totalMembers) {
			return;
		}

		const guildSnapshots = this.lastCompleteItems[guildId] ?? {};
		this.lastCompleteItems = {
			...this.lastCompleteItems,
			[guildId]: {
				...guildSnapshots,
				[listId]: new Map(items),
			},
		};
	}

	private getIncompleteListStreak(guildId: string, listId: string): number {
		return this.incompleteListStreaks[guildId]?.[listId] ?? 0;
	}

	private setIncompleteListStreak(guildId: string, listId: string, streak: number): void {
		const guildStreaks = this.incompleteListStreaks[guildId] ?? {};
		this.incompleteListStreaks = {
			...this.incompleteListStreaks,
			[guildId]: {
				...guildStreaks,
				[listId]: streak,
			},
		};
	}

	private getChannelIdByListId(guildId: string, listId: string): string | null {
		const mappings = this.channelListIds[guildId];
		if (!mappings) {
			return null;
		}
		for (const [channelId, mappedListId] of Object.entries(mappings)) {
			if (mappedListId === listId) {
				return channelId;
			}
		}
		return null;
	}

	private convertRow(rawItem: {
		member?: {
			user: {id: string};
			presence?: {status?: string; custom_status?: GatewayCustomStatusPayload | null} | null;
		};
		group?: MemberListGroup;
	}): MemberListRow | null {
		if (rawItem.group) {
			return {
				type: 'group',
				group: rawItem.group,
			};
		}

		if (!rawItem.member?.user?.id) {
			return null;
		}

		return {
			type: 'member',
			userId: rawItem.member.user.id,
			presence: rawItem.member.presence ?? null,
		};
	}

	private convertItem(guildId: string, userId: string): MemberListItem | null {
		const member = GuildMemberStore.getMember(guildId, userId);
		if (member) {
			return {
				type: 'member',
				data: member,
			};
		}

		this.logger.warn('Member not found in store:', {guildId, userId});
		return null;
	}

	private buildListStateEntries(params: {
		guildId: string;
		rows: Map<number, MemberListRow>;
		groupLayouts: ReturnType<typeof buildMemberListLayout>;
		totalMembers: number;
	}): {
		items: Map<number, MemberListItem>;
		presences: Map<string, StatusType>;
		customStatuses: Map<string, CustomStatus | null>;
		duplicateUserIds: Array<string>;
		missingUserIds: Array<string>;
	} {
		const {guildId, rows, groupLayouts, totalMembers} = params;
		const sortedRows = Array.from(rows.entries()).sort(([left], [right]) => left - right);
		const items = new Map<number, MemberListItem>();
		const presences = new Map<string, StatusType>();
		const customStatuses = new Map<string, CustomStatus | null>();
		const seenUserIds = new Set<string>();
		const duplicateUserIds: Array<string> = [];
		const missingUserIds: Array<string> = [];

		for (const [rowIndex, row] of sortedRows) {
			if (row.type !== 'member' || !row.userId) {
				continue;
			}

			let memberIndex: number | null = null;
			if (groupLayouts.length === 0) {
				if (rowIndex >= totalMembers) {
					continue;
				}
				memberIndex = rowIndex;
			} else {
				const layout = getGroupLayoutForRow(groupLayouts, rowIndex);
				if (!layout || rowIndex === layout.headerRowIndex) {
					continue;
				}
				const resolvedMemberIndex = layout.memberStartIndex + (rowIndex - layout.headerRowIndex - 1);
				if (resolvedMemberIndex < 0 || resolvedMemberIndex >= totalMembers) {
					continue;
				}
				memberIndex = resolvedMemberIndex;
			}

			if (seenUserIds.has(row.userId)) {
				duplicateUserIds.push(row.userId);
				continue;
			}
			seenUserIds.add(row.userId);

			const memberItem = this.convertItem(guildId, row.userId);
			if (memberItem) {
				items.set(memberIndex, memberItem);
			} else {
				missingUserIds.push(row.userId);
			}

			const presenceStatus = this.extractPresenceFromRow(row);
			if (presenceStatus) {
				presences.set(row.userId, presenceStatus);
			}

			if (row.presence && Object.hasOwn(row.presence, 'custom_status')) {
				const customStatus = fromGatewayCustomStatus(row.presence.custom_status ?? null);
				customStatuses.set(row.userId, customStatus);
			}
		}

		return {items, presences, customStatuses, duplicateUserIds, missingUserIds};
	}

	private queueMissingMemberLoad(guildId: string, storageKey: string, missingUserIds: Array<string>): void {
		const existingPending = this.pendingMemberLoads[guildId] ?? new Set<string>();
		const toLoad = missingUserIds.filter((userId) => !existingPending.has(userId));
		if (toLoad.length === 0) {
			return;
		}

		this.logger.debug('Queueing missing member list load', {
			guildId,
			listId: storageKey,
			requestedCount: missingUserIds.length,
			newlyQueuedCount: toLoad.length,
			userIdsSample: toLoad.slice(0, 25),
		});

		const nextPending = new Set(existingPending);
		for (const userId of toLoad) {
			nextPending.add(userId);
		}
		this.pendingMemberLoads = {...this.pendingMemberLoads, [guildId]: nextPending};

		void GuildMemberStore.ensureMembersLoaded(guildId, toLoad)
			.then(() => {
				this.logger.debug('Missing member list load completed', {
					guildId,
					listId: storageKey,
					loadedCount: toLoad.length,
					userIdsSample: toLoad.slice(0, 25),
				});
			})
			.catch((error: unknown) => {
				this.logger.warn('Failed to load missing member list entries:', {
					guildId,
					listId: storageKey,
					count: toLoad.length,
					error: error instanceof Error ? error.message : String(error),
				});
			})
			.finally(() => {
				const currentPending = this.pendingMemberLoads[guildId];
				if (currentPending) {
					const updatedPending = new Set(currentPending);
					for (const userId of toLoad) {
						updatedPending.delete(userId);
					}

					if (updatedPending.size === 0) {
						const {[guildId]: _, ...remainingPending} = this.pendingMemberLoads;
						this.pendingMemberLoads = remainingPending;
					} else {
						this.pendingMemberLoads = {...this.pendingMemberLoads, [guildId]: updatedPending};
					}
				}

				this.rebuildListItems(guildId, storageKey);
			});
	}

	private rebuildListItems(guildId: string, storageKey: string): void {
		const guildLists = this.lists[guildId];
		const listState = guildLists?.[storageKey];
		if (!listState) {
			return;
		}

		const groupLayouts = buildMemberListLayout(listState.groups);
		const totalMembers = Math.max(listState.memberCount, getTotalMemberCount(listState.groups));
		const {items, duplicateUserIds} = this.buildListStateEntries({
			guildId,
			rows: listState.rows,
			groupLayouts,
			totalMembers,
		});
		const continuityItems = this.applyContinuityBackfill({
			guildId,
			listId: storageKey,
			nextItems: items,
			previousItems: listState.items,
			totalMembers,
			hasDuplicateRows: duplicateUserIds.length > 0,
		});

		this.logger.debug('Rebuilt member list items', {
			guildId,
			listId: storageKey,
			rows: listState.rows.size,
			rebuiltItems: continuityItems.size,
			memberCount: listState.memberCount,
		});

		if (duplicateUserIds.length > 0) {
			const uniqueDuplicateUserIds = Array.from(new Set(duplicateUserIds));
			this.logger.warn('Duplicate member rows received while rebuilding list items:', {
				guildId,
				listId: storageKey,
				duplicateCount: uniqueDuplicateUserIds.length,
				userIds: uniqueDuplicateUserIds.slice(0, 25),
			});
		}

		const nextListState: MemberListState = {
			...listState,
			items: continuityItems,
		};
		this.lists = {
			...this.lists,
			[guildId]: {
				...guildLists,
				[storageKey]: nextListState,
			},
		};
		this.captureLastCompleteItemsSnapshot(guildId, storageKey, continuityItems, totalMembers);
	}

	private extractPresenceFromRow(row: MemberListRow): StatusType | null {
		const status = row.presence?.status;
		if (!status) {
			return null;
		}
		return this.normalizeStatus(status);
	}

	private normalizeStatus(status: string): StatusType {
		switch (status.toLowerCase()) {
			case 'online':
				return StatusTypes.ONLINE;
			case 'idle':
				return StatusTypes.IDLE;
			case 'dnd':
				return StatusTypes.DND;
			default:
				return StatusTypes.OFFLINE;
		}
	}

	subscribeToChannel(guildId: string, channelId: string, ranges: Array<[number, number]>): void {
		if (this.isMemberListUpdatesDisabled(guildId)) {
			return;
		}

		const storageKey = this.resolveListKey(guildId, channelId);
		const socket = GatewayConnectionStore.socket;

		const existingGuildLists = this.lists[guildId] ?? {};
		const guildLists: Record<string, MemberListState> = {...existingGuildLists};
		const existingList = guildLists[storageKey];
		const shouldSendUpdate = !areRangesEqual(existingList?.subscribedRanges, ranges);

		if (shouldSendUpdate) {
			this.logger.debug('Updating member list subscription ranges', {
				guildId,
				channelId,
				listId: storageKey,
				ranges,
			});
			socket?.updateGuildSubscriptions({
				subscriptions: {
					[guildId]: {
						member_list_channels: {[channelId]: ranges},
					},
				},
			});
		}

		if (!existingList) {
			guildLists[storageKey] = {
				memberCount: 0,
				onlineCount: 0,
				groups: [],
				rows: new Map(),
				items: new Map(),
				subscribedRanges: ranges,
				presences: new Map(),
				customStatuses: new Map(),
			};
		} else {
			guildLists[storageKey] = {...existingList, subscribedRanges: ranges};
		}

		this.touchList(guildId, storageKey);
		this.lists = {...this.lists, [guildId]: guildLists};
	}

	unsubscribeFromChannel(guildId: string, channelId: string): void {
		this.logger.debug('Unsubscribing from member list channel', {guildId, channelId});
		const socket = GatewayConnectionStore.socket;
		socket?.updateGuildSubscriptions({
			subscriptions: {
				[guildId]: {
					member_list_channels: {[channelId]: []},
				},
			},
		});

		const storageKey = this.resolveListKey(guildId, channelId);
		const existingGuildLists = this.lists[guildId] ?? {};
		const existingList = existingGuildLists[storageKey];

		if (existingList) {
			const guildLists = {...existingGuildLists};
			guildLists[storageKey] = {...existingList, subscribedRanges: []};
			this.lists = {...this.lists, [guildId]: guildLists};
		}
		this.setIncompleteListStreak(guildId, storageKey, 0);
	}

	getSubscribedRanges(guildId: string, channelId: string): Array<[number, number]> {
		const storageKey = this.resolveListKey(guildId, channelId);
		return this.lists[guildId]?.[storageKey]?.subscribedRanges ?? [];
	}

	getVisibleItems(guildId: string, listId: string, range: [number, number]): Array<MemberListItem> {
		const storageKey = this.resolveListKey(guildId, listId);
		const listState = this.lists[guildId]?.[storageKey];
		if (!listState) {
			return [];
		}

		const [start, end] = range;
		const items: Array<MemberListItem> = [];

		for (let i = start; i <= end; i++) {
			const item = listState.items.get(i);
			if (item) {
				items.push(item);
			}
		}

		return items;
	}

	getList(guildId: string, listId: string): MemberListState | undefined {
		const storageKey = this.resolveListKey(guildId, listId);
		return this.lists[guildId]?.[storageKey];
	}

	getMemberCount(guildId: string, listId: string): number {
		const storageKey = this.resolveListKey(guildId, listId);
		return this.lists[guildId]?.[storageKey]?.memberCount ?? 0;
	}

	getOnlineCount(guildId: string, listId: string): number {
		const storageKey = this.resolveListKey(guildId, listId);
		return this.lists[guildId]?.[storageKey]?.onlineCount ?? 0;
	}

	getPresence(guildId: string, listId: string, userId: string): StatusType | null {
		const storageKey = this.resolveListKey(guildId, listId);
		const listState = this.lists[guildId]?.[storageKey];
		if (!listState) {
			return null;
		}
		return listState.presences.get(userId) ?? null;
	}

	getCustomStatus(guildId: string, listId: string, userId: string): CustomStatus | null | undefined {
		const storageKey = this.resolveListKey(guildId, listId);
		const listState = this.lists[guildId]?.[storageKey];
		if (!listState) {
			return undefined;
		}
		if (!listState.customStatuses.has(userId)) {
			return undefined;
		}
		return listState.customStatuses.get(userId) ?? null;
	}

	private isMemberListUpdatesDisabled(guildId: string): boolean {
		const guild = GuildStore.getGuild(guildId);
		if (!guild) {
			return false;
		}
		return (guild.disabledOperations & GuildOperations.MEMBER_LIST_UPDATES) !== 0;
	}

	private touchList(guildId: string, listId: string): void {
		const now = Date.now();
		if (!this.lastAccess[guildId]) {
			this.lastAccess[guildId] = {};
		}
		this.lastAccess[guildId][listId] = now;
	}

	private resolveListKey(guildId: string, listIdOrChannelId: string): string {
		const guildMappings = this.channelListIds[guildId];
		return guildMappings?.[listIdOrChannelId] ?? listIdOrChannelId;
	}

	private registerChannelListId(guildId: string, channelId: string, listId: string): void {
		const guildMappings = this.channelListIds[guildId] ?? {};
		if (guildMappings[channelId] === listId) {
			if (!this.channelListIds[guildId]) {
				this.channelListIds = {...this.channelListIds, [guildId]: guildMappings};
			}
			return;
		}

		this.channelListIds = {
			...this.channelListIds,
			[guildId]: {...guildMappings, [channelId]: listId},
		};
	}

	private startPruneInterval(): void {
		if (this.pruneIntervalId != null) {
			return;
		}
		this.pruneIntervalId = window.setInterval(() => this.pruneExpiredLists(), MEMBER_LIST_PRUNE_INTERVAL_MS);
	}

	private pruneExpiredLists(): void {
		if (!WindowStore.focused) {
			return;
		}

		const now = Date.now();
		const ttlCutoff = now - MEMBER_LIST_TTL_MS;
		const updatedLists: Record<string, Record<string, MemberListState>> = {...this.lists};
		const updatedAccess: Record<string, Record<string, number>> = {...this.lastAccess};
		const updatedMappings: Record<string, Record<string, string>> = {...this.channelListIds};

		Object.entries(this.lastAccess).forEach(([guildId, accessMap]) => {
			const guildLists = {...(updatedLists[guildId] ?? {})};
			const guildAccess = {...accessMap};
			const guildMappings = {...(updatedMappings[guildId] ?? {})};

			Object.entries(accessMap).forEach(([listId, lastSeen]) => {
				const listState = guildLists[listId];
				if (listState && listState.subscribedRanges.length > 0) {
					guildAccess[listId] = now;
					return;
				}

				if (lastSeen < ttlCutoff) {
					delete guildLists[listId];
					delete guildAccess[listId];

					Object.entries(guildMappings).forEach(([channelId, mappedListId]) => {
						if (mappedListId === listId) {
							delete guildMappings[channelId];
							const socket = GatewayConnectionStore.socket;
							socket?.updateGuildSubscriptions({
								subscriptions: {
									[guildId]: {
										member_list_channels: {[channelId]: []},
									},
								},
							});
						}
					});
				}
			});

			if (Object.keys(guildLists).length === 0) {
				delete updatedLists[guildId];
			} else {
				updatedLists[guildId] = guildLists;
			}

			if (Object.keys(guildAccess).length === 0) {
				delete updatedAccess[guildId];
			} else {
				updatedAccess[guildId] = guildAccess;
			}

			if (Object.keys(guildMappings).length === 0) {
				delete updatedMappings[guildId];
			} else {
				updatedMappings[guildId] = guildMappings;
			}
		});

		this.lists = updatedLists;
		this.lastAccess = updatedAccess;
		this.channelListIds = updatedMappings;
	}
}

export default new MemberSidebarStore();
